import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket, Trophy, Gift, Users, Crown, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import PublicNav from "../components/PublicNav";
import { useAuth } from "../context/AuthContext";
import { LotteryCountdown } from "../components/LotteryCountdown";
import api from "../lib/api";
import { logError } from "../lib/logger";

const winnerName = (d) =>
  [d.winner_first_name, d.winner_last_name].filter(Boolean).join(" ") || d.winner_name || "Utente";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function Lottery() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/lottery").then((r) => setData(r.data)).catch((e) => logError("lottery", e));
  }, []);

  const draws = data?.draws || [];
  const isVip = user && (user.is_premium || user.role === "admin");

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {user ? <Navbar /> : <PublicNav />}
      <main className="max-w-4xl mx-auto px-6 py-14 lg:py-20" data-testid="lottery-page">
        <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 mb-6">
          <Ticket size={13} /> LOTTERIA RAEIN
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-4">
          Estrazione <span className="text-amber-400">premi</span> per gli abbonati VIP
        </h1>
        <p className="text-lg text-zinc-300 leading-relaxed max-w-2xl">
          Ogni iscritto ha un <span className="text-white font-medium">numero fortunato univoco</span> tra {data?.range?.min ?? 100} e {data?.range?.max ?? 5000}.
          All'estrazione partecipano <span className="text-white font-medium">solo gli abbonati VIP attivi</span>: se il tuo numero viene sorteggiato, vinci il premio.
        </p>

        {/* Countdown + prize */}
        <div className="mt-10 rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.07] to-transparent p-6 sm:p-8" data-testid="lottery-hero-card">
          <div className="flex items-center gap-2 text-sm text-zinc-400 mb-3">
            <Clock size={15} className="text-amber-400" /> Prossima estrazione
          </div>
          <LotteryCountdown target={data?.next_draw_at} />
          {data?.next_draw_at && (
            <div className="text-xs text-zinc-500 mt-3">{fmtDate(data.next_draw_at)}</div>
          )}
          {data?.prize ? (
            <div className="mt-6 flex items-start gap-3 rounded-lg bg-black/30 border border-white/10 p-4" data-testid="lottery-prize">
              <Gift size={20} className="text-amber-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">Premio in palio</div>
                <div className="text-white font-medium mt-0.5">{data.prize}</div>
              </div>
            </div>
          ) : null}
        </div>

        {/* User number + participants */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {user ? (
            <div className="rounded-xl border border-white/10 bg-[#141414] p-6" data-testid="lottery-my-number">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 mb-2">
                <Ticket size={14} className="text-amber-400" /> Il tuo numero
              </div>
              <div className="font-mono text-5xl font-bold text-amber-400">
                {user.lottery_number ?? "—"}
              </div>
              <div className={`mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
                isVip ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" : "bg-zinc-500/10 text-zinc-400 border-white/10"}`}>
                <Crown size={12} />
                {isVip ? "Partecipi all'estrazione" : "Attiva il VIP per partecipare"}
              </div>
              {!isVip && (
                <Link to="/pricing" data-testid="lottery-go-premium"
                  className="mt-4 inline-flex text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors">
                  Passa a VIP →
                </Link>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-[#141414] p-6" data-testid="lottery-login-cta">
              <div className="text-sm text-zinc-300">Accedi per scoprire il tuo numero fortunato.</div>
              <Link to="/auth" data-testid="lottery-login-btn"
                className="mt-4 inline-flex text-sm font-medium px-4 py-2 rounded-md bg-white text-black hover:bg-zinc-200 transition-colors">
                Accedi / Registrati
              </Link>
            </div>
          )}

          <div className="rounded-xl border border-white/10 bg-[#141414] p-6" data-testid="lottery-participants">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 mb-2">
              <Users size={14} className="text-blue-400" /> Partecipanti (VIP attivi)
            </div>
            <div className="font-mono text-5xl font-bold text-white">{data?.participants_count ?? 0}</div>
            <div className="mt-3 text-xs text-zinc-500">Numeri in gara per la prossima estrazione.</div>
          </div>
        </div>

        {/* History */}
        <div className="mt-12" data-testid="lottery-history">
          <div className="flex items-center gap-2 text-sm font-semibold text-white mb-4">
            <Trophy size={16} className="text-amber-400" /> Storico estrazioni
          </div>
          {draws.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 border border-dashed border-white/10 rounded-xl text-center">
              Nessuna estrazione effettuata finora.
            </div>
          ) : (
            <div className="space-y-3">
              {draws.map((d) => (
                <div key={d.id} data-testid={`lottery-draw-${d.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#141414] p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-center justify-center w-16 h-16 rounded-lg bg-amber-500/10 border border-amber-500/25">
                      <span className="text-[9px] uppercase tracking-wider text-zinc-500">N°</span>
                      <span className="font-mono text-xl font-bold text-amber-400">{d.drawn_number}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-white font-medium">
                        <Crown size={14} className="text-amber-400" /> {winnerName(d)}
                      </div>
                      {d.prize ? <div className="text-xs text-zinc-400 mt-0.5">Premio: {d.prize}</div> : null}
                      <div className="text-[11px] text-zinc-600 mt-0.5">{fmtDate(d.drawn_at)} · {d.participants_count} partecipanti</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="mt-10 text-[11px] text-zinc-600">
          ⓘ Le estrazioni sono riservate agli abbonati VIP con abbonamento attivo al momento del sorteggio. Data e ora sono stabilite dallo staff.
        </p>
      </main>
    </div>
  );
}
